import { hasFlag } from '@ofmos/shared/roles';
import { useEffect, useRef } from 'react';
import type { ObjectMetadata } from '../../../data/client';
import { ApiError } from '../../../data/http/transport';
import { formatNumber } from '../../../fields/format';
import {
  Button,
  type ButtonSize,
  DropdownLabel,
  DropdownMenu,
  DropdownRadioGroup,
  DropdownRadioItem,
} from '../../../ui';
import { useSession } from '../../auth/useSession';
import { PAGE_SIZE_CHOICES } from './pageSize';
import { atLoadedRowsCeiling, usableTotalCount } from './tableModel';

/**
 * THE STATUS ROW (`docs/decisions/table-completion.md` D8, D9, D10, D11, D26).
 *
 * The sentence at the end of the flat list: how many rows are on screen, how many there are
 * in the whole filtered set, and - when the list stops before the filter does - what to do
 * about it. It exists because the flat table's fifty-row silence is the last cap in this
 * product that neither truncates visibly nor refuses: record 51 did not exist as far as the
 * screen was concerned, and nothing on the screen said so.
 *
 * ONE COMPONENT, TWO LAYOUTS, so the phone cannot drift from the desktop (D11). `bar` is the
 * strip on the bottom edge of the desktop table's card, OUTSIDE its scrollport; `list-item`
 * is the card list's final `<li>`. Same shapes, same numbers, same page size - which is the
 * whole reason the two viewports share an implementation rather than a specification.
 *
 * FIVE SHAPES AND A PRECEDENCE, and the precedence is the part that took a rework (D8). On a
 * list of exactly `TABLE_LOADED_ROWS_MAX` matching rows the ceiling's condition and
 * completeness are BOTH true, and resolved the wrong way the screen prints "the first 1,000
 * of 1,000, narrow the filter" on a list that is complete - a false sentence and a useless
 * instruction, on the screen this module exists to make honest. So: a failure is the most
 * specific thing the row can say, then completeness, then the ceiling, then a counted
 * sentence, then an uncounted one.
 *
 * EVERY NUMBER IN EVERY SHAPE IS THE DISPLAYED ROW COUNT (D3). The ceiling counts FETCHED
 * rows because that is what bounds the DOM and the refetch; the sentence names what is on the
 * screen, so at the ceiling with two duplicates removed it reads `the first 998`. Naming the
 * constant there would be the first wrong number this module printed.
 *
 * ALWAYS ON SCREEN ON THE DESKTOP, and that is the wave-2 reversal paying for itself (D8,
 * section 12). D8 wrote this row as a `<tbody>` inside the table and named the cost out
 * loud: with the PAGE as the table's scrollport a persistent band would have floated over
 * data, which DESIGN.md's chrome/data split forbids, so "on a long walk the count is only
 * reachable by scrolling to the end". The table pans inside its card now, so the strip sits
 * on the card's bottom EDGE - outside the scrollport, over nothing, floating over nothing -
 * and the count is true at every scroll position. Not sticky: it does not have to be.
 *
 * THE SENTENCE AND THE CONTROLS READ LEFT TO RIGHT, never spread to the strip's edges. A
 * right-aligned figure under a table lands under whichever column happens to be last, which
 * reads as that column's value - the one thing a table full of numbers must not do
 * (`GroupedRecordTable`'s group header states the same rule for the same reason).
 */
export type RecordListFooterLayout = 'bar' | 'list-item';

export interface RecordListFooterProps {
  readonly object: ObjectMetadata;
  readonly layout: RecordListFooterLayout;
  /** Rows ON SCREEN - `pagedIds(...).length`, so after D12's dedupe. Every sentence's number. */
  readonly displayedRows: number;
  /** Pages FETCHED. With the page size, this is what D3's ceiling counts. */
  readonly pageCount: number;
  readonly pageSize: number;
  /** The sibling count's answer, raw: what may be PRINTED is D9's decision, taken below. */
  readonly totalCount: number | undefined;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  /** D21's Archived segment: the sentence says "archived customers", like every other string there. */
  readonly archived: boolean;
  /** The paged query's error, and only while the table still holds rows (D26). */
  readonly error: Error | null;
  readonly onShowMore: () => void;
  readonly onRetry: () => void;
  readonly onRestart: () => void;
  readonly onPageSizeChange: (next: number) => void;
}

/** What the row is saying, resolved once so the sentence and the controls cannot disagree. */
type FooterShape =
  | { readonly kind: 'failed'; readonly staleCursor: boolean }
  | { readonly kind: 'complete' }
  | { readonly kind: 'ceiling' }
  | { readonly kind: 'counted'; readonly total: number }
  | { readonly kind: 'uncounted' };

/**
 * D8'S PRECEDENCE, as the first matching arm. Read it as the document states it: the count
 * NEVER decides which shape renders, only whether a denominator is printed. Gating the
 * ceiling on `totalCount > displayedRows` was the other candidate and it is wrong twice - it
 * puts a number this module itself calls untrustworthy in charge of whether `Show more`
 * disappears, and with the count missing at the ceiling it falls through to the shape that
 * renders that button, which D3 forbids.
 */
function shapeOf(input: {
  readonly error: Error | null;
  readonly hasNextPage: boolean;
  readonly pageCount: number;
  readonly pageSize: number;
  readonly displayedRows: number;
  readonly totalCount: number | undefined;
}): FooterShape {
  if (input.error !== null) {
    return { kind: 'failed', staleCursor: isStaleCursor(input.error) };
  }
  if (!input.hasNextPage) return { kind: 'complete' };
  if (atLoadedRowsCeiling(input.pageCount, input.pageSize)) return { kind: 'ceiling' };
  const total = usableTotalCount(input.displayedRows, input.totalCount);
  return total === undefined ? { kind: 'uncounted' } : { kind: 'counted', total };
}

/**
 * A CURSOR THE SERVER NO LONGER ACCEPTS, told apart from every other failure by the code the
 * transport promises rather than by a string a screen has to regex. Retrying it would answer
 * 400 forever, which is why this decides between `Try again` and a restart (D26).
 */
function isStaleCursor(error: Error): boolean {
  return error instanceof ApiError && error.code === 'invalid_state';
}

export function RecordListFooter({
  object,
  layout,
  displayedRows,
  pageCount,
  pageSize,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  archived,
  error,
  onShowMore,
  onRetry,
  onRestart,
  onPageSizeChange,
}: RecordListFooterProps) {
  const session = useSession();
  const statusRef = useRef<HTMLParagraphElement>(null);

  const shape = shapeOf({
    error,
    hasNextPage,
    pageCount,
    pageSize,
    displayedRows,
    totalCount,
  });

  /**
   * THE EXITS THE COPY NAMES MUST EXIST FOR THE READER WHO IS READING IT (D10). `ExportAction`
   * renders NOTHING without `EXPORT_CSV`, because "a control whose only outcome is a refusal is
   * noise on a screen somebody uses all day" - so a sentence pointing at a button that reader
   * cannot see is the same defect one layer up. The filter is always there.
   */
  const canExport = session.data != null && hasFlag(session.data.permissions, 'EXPORT_CSV');

  const showMore = shape.kind === 'counted' || shape.kind === 'uncounted';

  /**
   * FOCUS DOES NOT DROP ON `<body>` WHEN THE BUTTON GOES (D8, obligation 20).
   *
   * While there is another page the button stays mounted and keeps focus across an append -
   * nothing to decide. It unmounts in two cases: the page that COMPLETES the list, and the page
   * that reaches D3's ceiling. In both, a keyboard reader who just pressed it would be left on
   * `<body>` - the exact product-wide defect the dialog audit found and fixed
   * (`ios26-mechanisms.md`, "Sheet/dialog: focus returns to what opened it"). So focus moves to
   * the status paragraph, which is `tabIndex={-1}` for this reason and nothing else, and the
   * sentence explaining why the button is gone is the thing that gets read.
   *
   * NOT to a row: the first draft of D8 moved focus to "the title link of the first newly
   * appended row", which drops a keyboard user into the middle of a table they were reading the
   * end of, competes with the `role="status"` announcement, and diverges from the precedent it
   * invoked - the dialog audit restored focus to the element that HAD it, and the nearest thing
   * to that here is the container the button was in.
   *
   * A FAILED APPEND inherits the same move for the same reason: `Show more` is replaced by a
   * different control, so the element that had focus is gone either way, and the sentence that
   * explains it is what should be read.
   *
   * AND IT REPAIRS A DROPPED FOCUS RATHER THAN TAKING A LIVE ONE, which is the difference between
   * this and the version the wave-4 audit found. The button also unmounts for reasons the reader
   * did not cause: `recordInvalidations` refetches every loaded page with `refetch: 'all'`, so a
   * frame over an object somebody else is deleting from can turn `hasNextPage` false while the
   * reader is typing in a cell three rows up. Moving focus on that transition would take the
   * caret out of their editor, which is a worse defect than the one this exists to fix. So the
   * move is conditioned on focus having ACTUALLY been dropped - `document.body` is where the
   * browser puts it when a focused element is removed, and it is exactly the state
   * `ios26-mechanisms.md` names. An editor, a toolbar button or another row still holding focus
   * keeps it.
   */
  const hadShowMore = useRef(showMore);
  useEffect(() => {
    const dropped = document.activeElement === null || document.activeElement === document.body;
    if (hadShowMore.current && !showMore && dropped) statusRef.current?.focus();
    hadShowMore.current = showMore;
  }, [showMore]);

  /**
   * THE NOUN AGREES WITH THE NUMBER IT FOLLOWS, which is why this takes one.
   *
   * Section 16 writes every string with `{plural}`, and on a list of exactly one row that prints
   * "Showing all 1 customers." - reachable on any list a filter narrows to one match and on any
   * object holding one record. The number that governs is the LAST one in the sentence:
   * "Showing 1 of 1,240 customers" is plural because the set is, and "Showing all 1 customer" is
   * singular because the count is. Every plural form is
   * byte-identical to the decision's own strings; this only adds the singular the decision does
   * not spell out, on the register the product already uses for the same problem
   * (`groupModel`'s `groupCountLabel`, `csv/copy`'s `plural`, both inflecting on `count === 1`).
   */
  const noun = (count: number) =>
    `${archived ? 'archived ' : ''}${(count === 1 ? object.labelSingular : object.labelPlural).toLowerCase()}`;
  const shown = formatNumber(displayedRows);

  /**
   * A COUNT, NOT A SENTENCE ABOUT ONE (P4 round 2, finding F7; brief P4-5 names the form "12
   * of 14 leads"). It read "Showing 50 of 114 agencies." - a verb the reader does not need,
   * because the rows are on the screen above it, and a full stop on something that is not a
   * sentence. Finder's status bar says "24 items"; Mail's list header says "1 Message". The
   * two shapes that carry an INSTRUCTION keep their punctuation, because there a second
   * sentence follows: the ceiling and the failed append.
   *
   * ONE SENTENCE PER SHAPE, and shape 4 is ONE string for three different states - the count
   * has not arrived, the count failed, the count is known-stale - because from the reader's
   * side they are one condition: the screen does not know the total. Inventing three sentences
   * for "not yet", "it failed" and "it disagrees" would be telling the reader about our
   * statements rather than about their list. A `Showing 50 of ...` with a spinner where the
   * number goes is not a trustworthy denominator; it is the same silence this module exists to
   * remove, dressed as progress.
   */
  const sentence =
    shape.kind === 'failed'
      ? `${shown} ${noun(displayedRows)}. ${
          shape.staleCursor
            ? 'This list moved on while you were reading it.'
            : 'The next rows could not be loaded.'
        }`
      : shape.kind === 'complete'
        ? `All ${shown} ${noun(displayedRows)}`
        : shape.kind === 'ceiling'
          ? `${ceilingCount(shown, noun, usableTotalCount(displayedRows, totalCount), displayedRows)} ${
              canExport
                ? 'Narrow the filter to work with fewer, or export this view to work with all of them.'
                : 'Narrow the filter to work with fewer.'
            }`
          : shape.kind === 'counted'
            ? `${shown} of ${formatNumber(shape.total)} ${noun(shape.total)}`
            : `${shown} ${noun(displayedRows)}`;

  /**
   * ON THE PHONE THE CONTROLS FILL THE ROW'S OWN HEIGHT (D11, obligation 32). D11 sizes the
   * status row at `--touch-target` and obligation 32 asserts that the `Show more` CONTROL is
   * at least that tall. `Button`'s `md` reads `--touch-target-min`, which is 44px on a phone
   * since E5, so on that row the two agree by themselves; the class stays because the ROW is
   * `--touch-target` unconditionally. On the desktop the button carries the floor itself
   * (`Button`'s `sm` draws at 32px and is pressed at 40 through `.hit-expand`).
   */

  /**
   * THE DESKTOP ROW IS A CAPTION ROW (P4-5; owner, 2026-09-10: "Load more looks
   * unfinished"). What it was: a sentence, a ghost "Show 50 more" and a "50 per page" menu,
   * all three left-aligned in a strip on the card's bottom edge, which read as an unfinished
   * toolbar. What it is: the count in the secondary label at the leading edge and ONE
   * neutral capsule at the trailing end, with the rows-per-page setting moved to the view
   * options where the other settings are.
   *
   * THE PHONE KEEPS ITS ROW as it was (D11): rows per page is within reach there and nowhere
   * else, because the phone's view options are a sheet two taps away.
   */
  const bar = layout === 'bar';
  const controlSize: ButtonSize = bar ? 'sm' : 'md';
  const controlClass = bar ? undefined : 'touch-target';

  const paging = (
    <>
      {showMore && (
        /* THE KIT'S CONTENT-AREA PUSH BUTTON (wave 3, L-B2; owner-28 asked whether this one
           should be glass, and the answer is that Liquid Glass is the navigation layer's
           material - the button was wrong for the other reason, the hairline ring). */
        <Button
          variant="push"
          size={controlSize}
          className={controlClass}
          loading={isFetchingNextPage}
          onClick={onShowMore}
        >
          Show more
        </Button>
      )}
      {shape.kind === 'failed' &&
        (shape.staleCursor ? (
          /**
           * A STALE CURSOR GETS A RESTART, NOT A RETRY (D26). `invalid_state` is the cursor
           * refusal's own code and it is a permanent answer, so the way forward is to drop
           * the walk and fetch page one again.
           */
          <Button variant="push" size={controlSize} className={controlClass} onClick={onRestart}>
            Start over
          </Button>
        ) : (
          <Button
            variant="push"
            size={controlSize}
            className={controlClass}
            loading={isFetchingNextPage}
            onClick={onRetry}
          >
            Try again
          </Button>
        ))}
    </>
  );

  const body = bar ? (
    <>
      {/*
        `role="status"` and not `alert`: the page is usable and this is a notice about it,
        which is the reason `views/ViewIssueBanner` already gives for the same choice.
        Appending a page tells a screen reader "Showing 100 of 1,240 customers." instead of
        silently growing the table.

        Only the SENTENCE is inside the live region. The controls are siblings, so an append
        announces what changed rather than re-reading a button label with it.
      */}
      <p
        ref={statusRef}
        role="status"
        tabIndex={-1}
        className="list-footer-count focus-visible:outline-2 focus-visible:outline-accent"
      >
        {sentence}
      </p>
      {paging}
    </>
  ) : (
    <div className="flex min-h-(--touch-target) flex-wrap items-center gap-x-3 gap-y-2">
      <p
        ref={statusRef}
        role="status"
        tabIndex={-1}
        className="text-sm text-ink-muted focus-visible:outline-2 focus-visible:outline-accent"
      >
        {sentence}
      </p>

      {showMore && (
        <Button
          variant="ghost"
          size={controlSize}
          className={controlClass}
          loading={isFetchingNextPage}
          onClick={onShowMore}
        >
          Show more
        </Button>
      )}

      {shape.kind === 'failed' &&
        (shape.staleCursor ? (
          <Button
            variant="secondary"
            size={controlSize}
            className={controlClass}
            onClick={onRestart}
          >
            Start over
          </Button>
        ) : (
          <Button
            variant="secondary"
            size={controlSize}
            className={controlClass}
            loading={isFetchingNextPage}
            onClick={onRetry}
          >
            Try again
          </Button>
        ))}

      <PageSizeMenu
        size={controlSize}
        triggerClass={controlClass}
        pageSize={pageSize}
        onChange={onPageSizeChange}
      />
    </div>
  );

  if (layout === 'list-item') {
    return <li className="px-1 py-3">{body}</li>;
  }

  /**
   * `shrink-0`, because the pane it sits in is a flex column whose OTHER child is the
   * scrollport: without it a long sentence would be squeezed by the region it is describing.
   * The horizontal padding is the cell's own token, so the count starts on the same vertical
   * line as the first column's values, and the hairline above it is the separator role - the
   * card's border and fill went with the card (P4-1).
   */
  return <div className="list-footer shrink-0">{body}</div>;
}

/**
 * `The first 998 of 4,312 customers.` - or without the denominator, if D9 withheld it.
 *
 * THE FULL STOP STAYS ON THIS ONE (round 2, finding F7). The verbless form is what a count
 * reads like, and a count that ends there needs no punctuation; the ceiling and the failure
 * are the two shapes where an INSTRUCTION follows, and two sentences run into one another
 * without it.
 */
function ceilingCount(
  shown: string,
  noun: (count: number) => string,
  denominator: number | undefined,
  displayedRows: number,
): string {
  return denominator === undefined
    ? `The first ${shown} ${noun(displayedRows)}.`
    : `The first ${shown} of ${formatNumber(denominator)} ${noun(denominator)}.`;
}

/**
 * ROWS PER PAGE (D4), on the status row rather than in the toolbar or the Adjust sheet - which
 * is what puts it within reach on a phone (D11). The 375px toolbar row is documented as one
 * control away from breaking, the Adjust sheet's rows are the toolbar's four controls, and a
 * fifth control on either surface buys nothing this row does not.
 *
 * THE FOOTNOTE IS NOT DECORATION. `limit` is part of the list's cache key, so a new page size
 * is a new entry with no loaded pages: the walk starts again. That is correct - a walk is
 * defined by its page size - and saying so beats surprising somebody who had loaded 400 rows.
 * The second half exists because D4 PERSISTS the value, and a menu that stores something
 * without saying so is the defect D4's own rule names. "This browser" and not "we remember":
 * the sentence has to be true on the phone that has never seen the choice.
 */
function PageSizeMenu({
  size,
  triggerClass,
  pageSize,
  onChange,
}: {
  readonly size: ButtonSize;
  /** The phone's touch floor, for the reason the caller states. Absent on the desktop row. */
  readonly triggerClass: string | undefined;
  readonly pageSize: number;
  readonly onChange: (next: number) => void;
}) {
  return (
    <DropdownMenu
      align="start"
      /**
       * THE FOOTNOTE IS A SENTENCE, so the menu is bounded - and the pixels are the menu's
       * own business now (`ui-foundation.md` D1). The story is in `ui/DropdownMenu`: an
       * unwrapped footnote made this panel 830px wide on the desktop and pushed its own
       * last words off a 375px screen.
       */
      width="prose"
      trigger={
        /* The visible label IS the accessible name: "50 per page" says the state and the
           subject, so an `aria-label` here would only be a second, competing name. */
        <Button variant="ghost" size={size} className={triggerClass}>
          {`${formatNumber(pageSize)} per page`}
        </Button>
      }
    >
      <DropdownLabel>Rows per page</DropdownLabel>
      <DropdownRadioGroup
        value={String(pageSize)}
        onValueChange={(value) => {
          // Resolved against the offered list rather than parsed: the same discipline
          // `useLocalChoice` uses for a stored value, and it keeps the number's source one list.
          const chosen = PAGE_SIZE_CHOICES.find((choice) => String(choice) === value);
          if (chosen !== undefined) onChange(chosen);
        }}
      >
        {PAGE_SIZE_CHOICES.map((choice) => (
          <DropdownRadioItem key={choice} value={String(choice)}>
            {formatNumber(choice)}
          </DropdownRadioItem>
        ))}
      </DropdownRadioGroup>
      <p className="px-2.5 pt-2 pb-1 text-xs text-ink-muted">
        Changing this starts the list again from the first rows, and this browser remembers your
        choice.
      </p>
    </DropdownMenu>
  );
}
